import {
	keepPreviousData,
	queryOptions,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query';

import { parse } from '@tinyhttp/content-disposition';
import { api } from '#client';
import { handleError, parseSchema } from '#client/validation';

import { getDocumentTypes } from '../file';
import { Activity } from '../shared/activity.types';
import { Reasons } from '../shared/reasons.types';
import { ReportStatusBody, ReportSubmitBody } from '../shared/report.types';
import {
	CloseRiskBody,
	NewRiskResponse,
	ReopenRiskBody,
	RiskBody,
	RisksQuery,
	RisksResults,
	RiskStatuses,
	SingleRisk,
} from './risks.types';
import { statsQueries } from './stats';

const getRisks = (query: RisksQuery): Promise<RisksResults> =>
	api
		.get('/risks', {
			params: query,
		})
		.then(parseSchema(RisksResults));

const getOwnRisks = (query: RisksQuery): Promise<RisksResults> =>
	api
		.get('/risks/external', {
			params: query,
		})
		.then(parseSchema(RisksResults));

const getRiskById = (riskId: string): Promise<SingleRisk> =>
	api.get(`/risks/risk/${riskId}`).then(parseSchema(SingleRisk));

const getRiskReasons = (): Promise<Reasons> =>
	api.get(`/risks/riskreasons`).then(parseSchema(Reasons));

const getActivityLog = (riskId: string): Promise<Activity> =>
	api.get(`/risks/risk/${riskId}/activity-log`).then(parseSchema(Activity));

const getStatuses = (orgId: number) =>
	api
		.get('/risks/statuses', {
			params: {
				organisationId: orgId,
			},
		})
		.then(parseSchema(RiskStatuses));

const getReportStatus = (riskId: string) =>
	api
		.get(`/risks/risk/${riskId}/report/status`)
		.then(parseSchema(ReportStatusBody));

export const riskQueries = {
	all: ['risks'],
	lists: () => [...riskQueries.all, 'list'],
	list: (query: RisksQuery) =>
		queryOptions({
			queryKey: [...riskQueries.lists(), query],
			queryFn: () => getRisks(query),
			placeholderData: keepPreviousData,
		}),
	listOwn: (query: RisksQuery) =>
		queryOptions({
			queryKey: [...riskQueries.lists(), query, 'own'],
			queryFn: () => getOwnRisks(query),
			placeholderData: keepPreviousData,
		}),
	ids: () => [...riskQueries.all, 'id'],
	id: (
		riskId: string,
		refetch?: {
			refetchInterval?: (risk: SingleRisk) => number | false;
		},
	) =>
		queryOptions({
			queryKey: [...riskQueries.ids(), riskId],
			queryFn: async () => getRiskById(riskId),
			refetchInterval: (query) =>
				query.state.data && refetch?.refetchInterval?.(query.state.data),
		}),
	reportStatus: (
		riskId: string,
		enabled: boolean = true,
		refetch?: {
			refetchInterval?: (risk: ReportStatusBody) => number | false;
		},
	) =>
		queryOptions({
			queryKey: [...riskQueries.ids(), riskId, 'reportStatus'],
			queryFn: async () => getReportStatus(riskId),
			refetchInterval: (query) =>
				query.state.data && refetch?.refetchInterval?.(query.state.data),
			enabled,
		}),
	riskReasons: () =>
		queryOptions({
			queryKey: [...riskQueries.all, 'reasons'],
			queryFn: () => getRiskReasons(),
		}),
	documentTypes: () =>
		queryOptions({
			queryKey: [...riskQueries.all, 'types'],
			queryFn: () => getDocumentTypes(),
		}),
	activity: (riskId: string) =>
		queryOptions({
			queryKey: [...riskQueries.ids(), riskId, 'activity'],
			queryFn: () => getActivityLog(riskId),
		}),
	statuses: (orgId: number) =>
		queryOptions({
			queryKey: [...riskQueries.all, 'statuses', orgId],
			queryFn: () => getStatuses(orgId),
		}),
};

const newRisk = () =>
	api.post(`/risks/risk`, { json: {} }).then(parseSchema(NewRiskResponse));

export const useNewRiskMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => newRisk(),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: riskQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const updateRisk = (params: RiskBody) =>
	api.put(`/risks/risk`, { json: params });

export const useUpdateRiskMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: RiskBody) => updateRisk(params),
		onSuccess: (_, params) =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: riskQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...riskQueries.ids(), String(params.riskId)],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const closeRisk = (riskId: string, payload: CloseRiskBody) =>
	api.put(`/risks/risk/${riskId}/close`, { json: payload });

export const useMutateRiskClose = (riskId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: CloseRiskBody) => closeRisk(riskId, params),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: riskQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...riskQueries.ids(), riskId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const reopenRisk = (riskId: string, payload: ReopenRiskBody) =>
	api.put(`/risks/risk/${riskId}/reopen`, { json: payload });

export const useMutateRiskReopen = (riskId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: ReopenRiskBody) => reopenRisk(riskId, params),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: riskQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...riskQueries.ids(), riskId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const submitReport = (riskId: string, payload: ReportSubmitBody) =>
	api.put(`/risks/risk/${riskId}/report/submit`, { json: payload });

export const useMutateSubmitReport = (riskId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: ReportSubmitBody) => submitReport(riskId, params),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: riskQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...riskQueries.ids(), riskId],
				}),
				queryClient.invalidateQueries({
					queryKey: statsQueries.all,
				}),
			]),
	});
};

const getExtractFile = (riskId: number, subset?: 'details' | 'data') =>
	api.get(
		`/file/${
			subset === 'details' ? 'extract-risk-details'
			: subset === 'data' ? 'extract-risk-data'
			: 'extract-risk-files'
		}/${riskId}`,
	);

export const downloadExtractRiskFile = async (
	riskId: number,
	subset?: 'details' | 'data',
) => {
	const response = await getExtractFile(riskId, subset);

	const filename = parse(response.headers.get('content-disposition') as string)
		.parameters.filename;
	const type = response.headers.get('content-type') || 'application/zip';

	return new File(
		[await response.blob()],
		(typeof filename === 'string' && filename) ||
			`risk-${subset ?? 'extract'}-${riskId}.zip`,
		{ type },
	);
};

const getReportFile = (riskId: string) =>
	api.get(`/file/risk-report/${riskId}`);

export const downloadReportRiskFile = async (riskId: string) => {
	const response = await getReportFile(riskId);

	const filename = parse(response.headers.get('content-disposition') as string)
		.parameters.filename;
	const type = response.headers.get('content-type') || 'application/zip';

	return new File(
		[await response.blob()],
		(typeof filename === 'string' && filename) || `risk-report-${riskId}.zip`,
		{ type },
	);
};

const bulkAssignRisks = (riskIds: number[], underwriterId: string) =>
	api
		.put('/risks/bulkassign', { json: { riskIds, underwriterId } })
		.catch(handleError(['riskIds', 'underwriterId']));

export const useBulkAssignRisksMutate = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (params: { riskIds: number[]; underwriterId: string }) =>
			bulkAssignRisks(params.riskIds, params.underwriterId),
		onSuccess: (_, variables) =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: riskQueries.lists(),
				}),
				...variables.riskIds.map((riskId) =>
					queryClient.invalidateQueries({
						queryKey: [...riskQueries.ids(), String(riskId)],
					}),
				),
			]),
	});
};

const continueRisk = (riskId: string) =>
	api.put(`/risks/risk/${riskId}/continue`).catch(handleError([]));

export const useContinueRiskMutate = (riskId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: () => continueRisk(riskId),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: riskQueries.lists(),
				}),
				queryClient.invalidateQueries({
					queryKey: [...riskQueries.ids(), riskId],
				}),
			]),
	});
};
